import { Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react'

import {
  alertsPages,
  type NavLocation,
  type PageRow,
  type SectionTabsModel,
} from '@/components/layout/nav-groups'
import { EXACT_ACTIVE } from '@/components/layout/nav-items'
import { useAlertCounts } from '@/components/layout/use-alert-counts'
import { motionDuration } from '@/lib/motion/motion-duration'
import { useReducedMotion } from '@/lib/motion/use-reduced-motion'
import { cn } from '@/lib/utils'

/** Room kept beside the page's tab when the strip scrolls it into view: past the arrow and the fade. */
const ACTIVE_MARGIN_REM = 3

/** How far an arrow scrolls: most of the strip, so the tab cut off at its edge stays in sight. */
const ARROW_STEP = 0.7

/**
 * The fade on whichever side still has tabs past it — none, the end, the start or both. A
 * `mask-image`, not the tables' `scroll-fade-x`: that paints card-coloured covers, and the strip
 * sits on the page.
 */
const FADE = {
  none: null,
  end: '[mask-image:linear-gradient(to_right,black_calc(100%-var(--strip-fade-w)),transparent)]',
  start: '[mask-image:linear-gradient(to_left,black_calc(100%-var(--strip-fade-w)),transparent)]',
  both: '[mask-image:linear-gradient(to_right,transparent,black_var(--strip-fade-w),black_calc(100%-var(--strip-fade-w)),transparent)]',
} as const

const rootFontSize = () =>
  Number.parseInt(getComputedStyle(document.documentElement).fontSize) || 16

interface Overflow {
  /** Tabs past the strip's start edge. */
  readonly before: boolean
  /** Tabs past its end edge. */
  readonly after: boolean
}

const NO_OVERFLOW: Overflow = { before: false, after: false }

/**
 * The strip scrolling from `from` to `to` to bring the page's tab into view. One object for the
 * whole switch: a strip taking over holds the same one, so the clock it starts is theirs too.
 */
interface Reveal {
  readonly from: number
  readonly to: number
  /** `performance.now()` when it began: when it was set up, then its first frame's. */
  at: number
  /**
   * Its clock runs from the first frame drawn after the switch, not from the click: the commit
   * in between can take a few frames' time, and an ease-out already well under way by then
   * would open with a jump.
   */
  started: boolean
}

/** What a strip shows: its section, the page's tab, and the reveal of that tab under way. */
interface StripState {
  readonly section: string
  readonly key: string
  readonly reveal: Reveal | null
}

/** The strip the member saw last, kept past its page: see `takesOver`. */
interface StripMemory extends StripState {
  /** The strip that wrote it (`useId`): only that one moves its scroll or marks it ended. */
  readonly owner: string
  scrollLeft: number
  /** When that strip unmounted; `null` while it is still mounted. */
  endedAt: number | null
}

let lastStrip: StripMemory | null = null

/**
 * How soon after the last strip unmounted a new one still takes over from it: the two are one
 * navigation's, as a pending page and its loaded page are (page-frame.ts, the same window).
 */
const TAKEOVER_WINDOW_MS = 100

/**
 * Whether a strip mounting now carries on from the last one: the same section, and that strip
 * still mounted or only just gone. Each section page is its own route, so a tab click mounts a
 * new strip — the pending page's, then the loaded page's — while the old page's is still up
 * (hidden) or leaving in the same commit. A strip after a page with none (Home, a stock page)
 * or a different section's starts fresh: its page's tab brought into view at once.
 */
const takesOver = (memory: StripMemory | null, section: string, now: number) =>
  memory !== null &&
  memory.section === section &&
  (memory.endedAt === null || now - memory.endedAt <= TAKEOVER_WINDOW_MS)

let timing: { duration: number; easing: string } | null = null

/** The reveal's pace: the motion kit's `--motion-slow` and `--ease-out`, read once. */
function revealTiming() {
  timing ??= {
    duration: motionDuration('--motion-slow', 480),
    easing:
      getComputedStyle(document.documentElement).getPropertyValue('--ease-out').trim() ||
      'ease-out',
  }
  return timing
}

/**
 * Where a reveal has the strip at `now`: at its start until its first frame, then along the
 * easing — the browser's own reading of the token, from an animation with no target. With no Web
 * Animations (jsdom) a started reveal is already over.
 */
function revealAt(reveal: Reveal, now: number): number {
  if (!reveal.started) return reveal.from
  const { duration, easing } = revealTiming()
  const elapsed = now - reveal.at
  if (elapsed >= duration || typeof Animation === 'undefined') return reveal.to
  const probe = new Animation(new KeyframeEffect(null, null, { duration, easing }))
  probe.pause()
  probe.currentTime = Math.max(0, elapsed)
  const progress = probe.effect?.getComputedTiming().progress ?? 1
  return reveal.from + (reveal.to - reveal.from) * progress
}

const revealing = (reveal: Reveal | null, now: number): reveal is Reveal =>
  reveal !== null && now - reveal.at < revealTiming().duration

interface SectionTabsProps {
  /** The section's name: the strip is the "{label} pages" navigation. */
  label: string
  tabs: readonly PageRow[]
  /**
   * `shell`: ScreenLayout's, across the top of the main column, drawn from `lg` up only — below
   * it the drawer and the bottom bar switch pages and the page title comes back (owner D9).
   * `inline`: inside a page, at every width — the stock page's, under its company head.
   */
  placement?: 'shell' | 'inline'
  className?: string | undefined
}

/**
 * The section tab strip (pages revamp, 2026-10-27; COMPARE-concept § Canonical patterns): the
 * section's pages as real links, the page on screen `aria-current="page"` at 600, the rest at
 * 400, each label holding its bold width (`reserve-bold`) so lighting one never shifts the row.
 * 18/24 on a 40px row over a hairline, 20 between labels. The inks are the mock's strip's
 * (`--strip-tab*`: the full secondary for the rest, the full primary on hover and for the page
 * on screen), and ease across `--motion-fast`; the weight changes at once. No underline: the
 * mock hides its tab bar on this strip.
 *
 * It scrolls sideways when the tabs outrun the column (Market's fourteen): a fade and an arrow on
 * each side that has more — the arrow steps 70% of the strip, smoothly unless motion is reduced —
 * and the page's own tab is scrolled into view on arrival. The wheel is left alone: a vertical
 * wheel over the strip scrolls the page, never the strip. On a touch screen the arrows go and a
 * finger drags it, and each tab grows to a 44px target.
 *
 * Switching pages keeps the strip where it was (owner, 2026-10-27: the tabs blinked on every
 * switch). Each section page is its own route, so the switch mounts a new strip; the last one is
 * remembered (`lastStrip`), and a new strip of the same section opens at its scroll. When the
 * new page's tab is out of sight from there, the strip scrolls it into view, on the motion kit's
 * pace and timed from when the switch began — so the pending page's strip starts that scroll and
 * the loaded page's carries it on, never restarting it. Under the stock page's `?tab=`, Alerts'
 * `?status=` and Public Issues' `?type=` the strip stays mounted and scrolls in place. A first
 * visit, or a strip after another section's or none, brings its tab into view at once, as does
 * reduced motion.
 *
 * A Soon tab (a page not built yet, owner rule 2026-10-27) is muted and tagged "Soon": not a
 * link, `aria-disabled`, out of the tab order, with no hover.
 *
 * Every tab sits in its own `<li>` so a screen reader hears how many there are; the strip is a
 * `<nav>` named for its section, so it is reachable from the landmarks list, beside the page's
 * `h1` (kept for screen readers by TitleRow while the strip shows).
 */
export default function SectionTabs({
  label,
  tabs,
  placement = 'shell',
  className,
}: SectionTabsProps) {
  const instance = useId()
  const reduced = useReducedMotion()
  const scrollerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  /** What this strip shows, once it has been settled. */
  const shownRef = useRef<StripState | null>(null)
  /** The frame request driving a reveal, while one runs. */
  const frameRef = useRef<number | null>(null)
  const [overflow, setOverflow] = useState<Overflow>(NO_OVERFLOW)
  const activeKey = tabs.find((tab) => tab.active)?.key

  const stopReveal = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    frameRef.current = null
    const shown = shownRef.current
    if (shown?.reveal) shownRef.current = { ...shown, reveal: null }
    if (lastStrip?.owner === instance) lastStrip = { ...lastStrip, reveal: null }
  }, [instance])

  // Runs `reveal` a frame at a time: each frame's scroll is read off the clock, so a strip that
  // picks a reveal up part-way lands where the one before it would have. `performance.now()`, not
  // the frame's timestamp: the first frame after a commit can be stamped before the commit began,
  // which put the strip back at the reveal's start for a frame.
  const runReveal = useCallback((scroller: HTMLElement, reveal: Reveal) => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    const tick = (frame: boolean) => {
      const now = performance.now()
      if (frame && !reveal.started) {
        reveal.started = true
        reveal.at = now
      }
      scroller.scrollTo({ left: revealAt(reveal, now), behavior: 'instant' })
      frameRef.current = revealing(reveal, now)
        ? requestAnimationFrame(() => {
            tick(true)
          })
        : null
    }
    tick(false)
  }, [])

  // The page's tab, settled on before the first paint. A strip taking over from the last one
  // (`takesOver`) opens where that one was, or where its reveal has got to, and carries a reveal
  // of this same tab on; otherwise, if the tab is out of sight, the strip scrolls to it — at
  // once on arrival or under reduced motion, else a reveal from where it stands. Skipped while
  // the strip is not laid out (below `lg` the shell's is `display: none`, and a page the router
  // keeps hidden behind its successor reads as zero); the observer below settles it once it has
  // a width.
  const settle = useCallback(() => {
    const scroller = scrollerRef.current
    if (scroller === null || activeKey === undefined || scroller.clientWidth === 0) return
    const tab = scroller.querySelector<HTMLElement>('[aria-current="page"]')
    if (tab === null) return
    const now = performance.now()
    const shown = shownRef.current?.section === label ? shownRef.current : null
    const memory = shown === null && takesOver(lastStrip, label, now) ? lastStrip : null
    const previous = shown ?? memory

    let reveal: Reveal | null = null
    if (previous !== null && previous.key === activeKey && revealing(previous.reveal, now)) {
      reveal = previous.reveal
    } else {
      if (memory !== null) {
        const left = revealing(memory.reveal, now)
          ? revealAt(memory.reveal, now)
          : memory.scrollLeft
        scroller.scrollTo({ left, behavior: 'instant' })
      }
      const to = revealLeft(scroller, tab)
      if (to !== null) {
        if (previous === null || reduced) scroller.scrollTo({ left: to, behavior: 'instant' })
        else reveal = { from: scroller.scrollLeft, to, at: now, started: false }
      }
    }
    if (reveal === null) {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    } else runReveal(scroller, reveal)

    const state: StripState = { section: label, key: activeKey, reveal }
    shownRef.current = state
    lastStrip = { ...state, owner: instance, scrollLeft: scroller.scrollLeft, endedAt: null }
  }, [activeKey, instance, label, reduced, runReveal])

  useLayoutEffect(() => {
    settle()
  }, [settle])

  useLayoutEffect(
    () => () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
      frameRef.current = null
      if (lastStrip?.owner === instance) lastStrip.endedAt = performance.now()
    },
    [instance],
  )

  useLayoutEffect(() => {
    const scroller = scrollerRef.current
    const list = listRef.current
    if (scroller === null || list === null) return

    const measure = () => {
      const shown = shownRef.current
      if (shown === null || shown.key !== activeKey) settle()
      else if (scroller.clientWidth > 0 && lastStrip?.owner === instance) {
        lastStrip.scrollLeft = scroller.scrollLeft
      }
      // 1px of slack: a fractional width can leave a sub-pixel the strip never scrolls.
      const before = scroller.scrollLeft > 1
      const after = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft > 1
      setOverflow((previous) =>
        previous.before === before && previous.after === after ? previous : { before, after },
      )
    }

    measure()
    scroller.addEventListener('scroll', measure, { passive: true })
    // A hand on the strip — a wheel, a drag, a finger — takes the scroll over from a reveal.
    const handsOn = ['wheel', 'pointerdown', 'touchstart'] as const
    handsOn.forEach((type) => {
      scroller.addEventListener(type, stopReveal, { passive: true })
    })
    // The box (a window or sidebar resize) and the row inside it (labels re-measured once the
    // web font lands) both change what overflows.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(scroller)
    observer?.observe(list)
    return () => {
      scroller.removeEventListener('scroll', measure)
      handsOn.forEach((type) => {
        scroller.removeEventListener(type, stopReveal)
      })
      observer?.disconnect()
    }
  }, [activeKey, instance, settle, stopReveal])

  const step = (direction: -1 | 1) => {
    const scroller = scrollerRef.current
    if (scroller === null) return
    stopReveal()
    // The scroller's own `scroll-smooth` (off under reduced motion) decides how it travels.
    scroller.scrollBy({ left: direction * ARROW_STEP * scroller.clientWidth })
  }

  const fade =
    overflow.before && overflow.after
      ? FADE.both
      : overflow.before
        ? FADE.start
        : overflow.after
          ? FADE.end
          : FADE.none

  return (
    <nav
      aria-label={`${label} pages`}
      // ScreenLayout's page entrance leaves the strip out of the frame's fade (PAGE ENTER).
      data-section-strip=""
      className={cn(
        'relative border-b border-border-subtle',
        placement === 'shell' && 'hidden lg:block',
        className,
      )}
    >
      <div
        ref={scrollerRef}
        // Its own id for the router's scroll restoration, one per mount, so the router never
        // finds it again. Left to match by DOM path, the router wrote the last page's scroll
        // into the next page's strip after `settle` had restored it and revealed the new tab,
        // and left that tab out of sight. The strip keeps its own place (`lastStrip`).
        data-scroll-restoration-id={instance}
        className={cn(
          'overflow-x-auto overscroll-x-contain scroll-smooth motion-reduce:scroll-auto',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          fade,
        )}
      >
        {/* `relative` makes the list the tabs' offset parent, so `offsetLeft` is measured from
            the strip's start, scrolled content included. */}
        <ul ref={listRef} className="relative flex w-max gap-5">
          {tabs.map((tab) => (
            <li key={tab.key} className="flex">
              <StripTab tab={tab} />
            </li>
          ))}
        </ul>
      </div>
      {overflow.before ? <StripArrow direction={-1} onStep={step} /> : null}
      {overflow.after ? <StripArrow direction={1} onStep={step} /> : null}
    </nav>
  )
}

/**
 * Where the strip must scroll to bring `tab` into view with `ACTIVE_MARGIN_REM` to spare, or
 * `null` when it is in view already. Held inside the scroll range, so a reveal eases all the way
 * to where the strip can actually go.
 */
function revealLeft(scroller: HTMLElement, tab: HTMLElement): number | null {
  const margin = ACTIVE_MARGIN_REM * rootFontSize()
  const start = tab.offsetLeft
  const end = start + tab.offsetWidth
  const furthest = Math.max(0, scroller.scrollWidth - scroller.clientWidth)
  let left: number | null = null
  if (start - margin < scroller.scrollLeft) left = start - margin
  else if (end + margin > scroller.scrollLeft + scroller.clientWidth) {
    left = end + margin - scroller.clientWidth
  }
  return left === null ? null : Math.min(furthest, Math.max(0, left))
}

/**
 * The row box every tab shares: 40 tall (44 on touch), the label a little above centre. Its
 * scroll margin is `ACTIVE_MARGIN_REM`'s, so a tab the keyboard walks to stops clear of the arrow
 * and the fade too.
 */
const TAB =
  'flex h-10 shrink-0 scroll-mx-12 items-center gap-2 pb-1 whitespace-nowrap text-h-strip pointer-coarse:h-11'

function StripTab({ tab }: { tab: PageRow }) {
  // The nav's own Title Case, as the sidebar spells it (owner, 2026-10-27: "titlecase, like All
  // Trades").
  const text = tab.label

  if (tab.soon === true) {
    return (
      <button
        type="button"
        aria-disabled="true"
        tabIndex={-1}
        className={cn(TAB, 'cursor-default font-normal text-strip-tab-soon')}
      >
        {/* The space keeps the name "Last Minute Movers Soon"; flex drops it from the layout. */}
        {text} <span className="text-body-xs">Soon</span>
      </button>
    )
  }

  const words = tab.count === undefined ? text : `${text} · ${String(tab.count)}`
  return (
    <Link
      {...tab.link}
      activeOptions={EXACT_ACTIVE}
      aria-current={tab.active ? 'page' : undefined}
      className={cn(
        TAB,
        // Inset: the strip scrolls, and a ring drawn outside a tab would be clipped by it.
        'transition-colors focus-visible:-outline-offset-2',
        // The mock's strip inks (`--strip-tab*`); a keyboard's tab lights as a hovered one does.
        tab.active ? 'text-strip-tab-active' : 'font-normal text-strip-tab',
        !tab.active && 'hover:text-strip-tab-hover focus-visible:text-strip-tab-hover',
      )}
    >
      <span data-text={words} className="reserve-bold">
        {words}
      </span>
    </Link>
  )
}

/**
 * An overflow arrow, pinned over the strip's edge on the page's own colour (32 × 36, the hairline
 * left showing under it). Out of the tab order — a keyboard walks the links, and the browser
 * scrolls each into view — and gone on a touch screen, where a finger drags the strip.
 */
function StripArrow({
  direction,
  onStep,
}: {
  direction: -1 | 1
  onStep: (direction: -1 | 1) => void
}) {
  const Chevron = direction === -1 ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={direction === -1 ? 'Show earlier tabs' : 'Show more tabs'}
      onClick={() => {
        onStep(direction)
      }}
      className={cn(
        'absolute top-0 flex h-9 w-8 items-center justify-center bg-background text-muted-foreground',
        'transition-colors hover:text-foreground pointer-coarse:hidden',
        direction === -1 ? 'left-0' : 'right-0',
      )}
    >
      <Chevron className="size-4" aria-hidden="true" />
    </button>
  )
}

/**
 * ScreenLayout's strip for the page's section (`sectionTabs`), with the Alerts counts filled in
 * on the Alerts pages — the one part of it that waits on data, and only for its numbers.
 *
 * rest:kit (owner G1, 2026-10-29) — the section's name sits above the strip as the mock's
 * `shell__parent`: 12/16 600 tertiary, +.24 tracking, pulled 4 up, at the mock's y76. The mock sets
 * it 2 off the strip (strip 14 lower); the grid makes that 4 (16 lower, the strip 2 under the
 * mock's). Hidden from assistive tech: the `<nav>` already says "{label} pages".
 * The wrapper carries `data-section-strip` so the label stands still with the strip through a page
 * switch (PAGE ENTER in globals.css). ScreenLayout's actions cell offsets itself by the same 16.
 */
export function ShellSectionTabs({
  location,
  model,
  className,
}: {
  location: NavLocation
  model: SectionTabsModel
  className?: string | undefined
}) {
  return (
    <div data-section-strip="" className={cn('hidden lg:block', className)}>
      <p aria-hidden="true" className="-mt-1 mb-1 text-section-label text-muted-foreground-subtle">
        {model.label}
      </p>
      {model.kind === 'alerts' ? (
        <AlertsSectionTabs location={location} label={model.label} />
      ) : (
        <SectionTabs label={model.label} tabs={model.tabs} />
      )}
    </div>
  )
}

function AlertsSectionTabs({ location, label }: { location: NavLocation; label: string }) {
  const counts = useAlertCounts()
  return <SectionTabs label={label} tabs={alertsPages(location, counts).visible} />
}
